import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import TextBox from 'devextreme-react/text-box';
import { useEffect, useMemo, useState } from 'react';
import {
  EMOJI_CATEGORIES,
  fallbackEmojiCatalog,
  loadEmojiCatalog,
  matchesEmoji,
  type CategorizedEmojiEntry,
  type EmojiCategoryId
} from './emojiCatalog';

interface MediaLibraryReactionPickerProps {
  disabled?: boolean;
  currentEmoji?: string | null;
  onSelect: (emoji: string) => void;
}

export function MediaLibraryReactionPicker({
  disabled,
  currentEmoji,
  onSelect
}: MediaLibraryReactionPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<EmojiCategoryId | 'all'>('all');
  const [catalog, setCatalog] = useState<CategorizedEmojiEntry[]>(() => fallbackEmojiCatalog());

  useEffect(() => {
    let cancelled = false;
    void loadEmojiCatalog().then((items) => {
      if (!cancelled) setCatalog(items);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return catalog.filter(
      (entry) =>
        (category === 'all' || entry.category === category) && matchesEmoji(entry, normalized)
    );
  }, [catalog, category, query]);

  return (
    <>
      <Button
        text="React"
        icon="like"
        stylingMode="outlined"
        disabled={disabled}
        onClick={() => setOpen(true)}
      />
      <Popup
        visible={open}
        onHiding={() => setOpen(false)}
        title="React"
        showCloseButton
        hideOnOutsideClick
        width={420}
        height={480}
        dragEnabled
        resizeEnabled
        wrapperAttr={{ class: 'media-lib-reaction-picker-popup' }}
      >
        <div className="media-lib-reaction-picker">
          <TextBox
            value={query}
            placeholder="Search emoji…"
            width="100%"
            mode="search"
            valueChangeEvent="input"
            onValueChanged={(e) => setQuery(String(e.value ?? ''))}
          />
          <div className="media-lib-reaction-picker__cats" role="tablist" aria-label="Emoji categories">
            {EMOJI_CATEGORIES.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={category === item.id}
                className={`media-lib-reaction-picker__cat${category === item.id ? ' is-active' : ''}`}
                onClick={() => setCategory(item.id)}
              >
                {item.shortLabel}
              </button>
            ))}
          </div>
          <div className="media-lib-reaction-picker__grid" role="listbox" aria-label="Emoji list">
            {filtered.length === 0 ? (
              <p className="utility-app__muted">No emoji match that search.</p>
            ) : (
              filtered.map((entry) => {
                const selected = currentEmoji === entry.emoji;
                return (
                  <button
                    key={`${entry.emoji}-${entry.name}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    title={entry.name}
                    className={`media-lib-reaction-picker__emoji${selected ? ' is-selected' : ''}`}
                    onClick={() => {
                      onSelect(entry.emoji);
                      setOpen(false);
                    }}
                  >
                    {entry.emoji}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Popup>
    </>
  );
}
